import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Button,
} from '@mui/material';
import { 
  People, 
  TrendingUp, 
  Inventory2, 
  ShoppingCart, 
  Business,
  PersonAdd,
  ArrowForward,
} from '@mui/icons-material';

const RepDashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  
  // Get user from sessionStorage if currentUser not provided
  const user = currentUser || (() => {
    try {
      const userStr = sessionStorage.getItem('barebiologics_currentUser');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  })();

  const userName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Sales Rep';
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase() || 'SR';

  // Determine base path dynamically
  const basePath = window.location.pathname.startsWith('/client_rep') ? '/client_rep' : '/rep';

  // TODO: Fetch actual stats from API
  const [stats, setStats] = useState({
    totalClients: 0,
    activeLeads: 0,
    ordersThisMonth: 0,
    totalRevenue: 0,
  });

  const StatCard = ({ title, value, icon, color, description, onClick }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: 'pointer', 
        transition: 'all 0.3s ease',
        border: '1px solid #ececef',
        '&:hover': { 
          boxShadow: 4,
          transform: 'translateY(-4px)',
          borderColor: color,
        } 
      }} 
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              color="text.secondary" 
              variant="body2" 
              component="div"
              sx={{ 
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h3" 
              component="div" 
              sx={{ 
                color, 
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                mb: 0.5
              }}
            >
              {value}
            </Typography>
            {description && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {description}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 2
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
          </Box>
        </Box>
        <Button
          endIcon={<ArrowForward />}
          size="small"
          sx={{
            mt: 1,
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            color: color,
            '&:hover': {
              backgroundColor: `${color}10`,
            }
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        pt: { xs: 7, md: 3 },
        minHeight: '100vh',
        maxWidth: '1400px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        pb: 6,
      }}
    >
      {/* Welcome Header */}
      <Card sx={{ 
        mb: 5, 
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)', 
        overflow: 'hidden', 
        position: 'relative',
        boxShadow: '0 4px 20px rgba(13, 148, 136, 0.2)'
      }}>
        <CardContent sx={{ p: { xs: 3, md: 4 }, position: 'relative' }}>
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 120,
              height: 120,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              right: 50,
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              zIndex: 0
            }}
          />

          <Grid container spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ 
                  mr: 3, 
                  width: { xs: 56, md: 64 }, 
                  height: { xs: 56, md: 64 }, 
                  bgcolor: 'rgba(255, 255, 255, 0.2)', 
                  border: '2px solid rgba(255, 255, 255, 0.3)' 
                }}>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                    {userInitials}
                  </Typography>
                </Avatar>
                <Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1, 
                      color: 'white',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }}
                  >
                    Welcome, {userName.split(' ')[0]}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 400, 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontFamily: 'Poppins, sans-serif'
                    }}
                  >
                    Sales Representative Portal
                  </Typography>
                  {user?.organization && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)', 
                        mt: 0.5,
                        fontFamily: 'Poppins, sans-serif'
                      }}
                    >
                      {user.organization}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  mb: 2, 
                  fontSize: '1.1rem',
                  fontFamily: 'Poppins, sans-serif'
                }}
              >
                Manage your clients, track leads, view orders, and monitor your sales performance all in one place.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PersonAdd />}
                onClick={() => navigate(`${basePath}/crm-leads`)}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontFamily: 'Poppins, sans-serif',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                  },
                  transition: 'all 0.3s ease',
                  mb: 2
                }}
              >
                Add New Lead
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="My Clients"
            value={stats.totalClients || '--'}
            icon={<People />}
            color="#0d9488"
            description="active clients"
            onClick={() => navigate(`${basePath}/clients`)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Leads"
            value={stats.activeLeads || '--'}
            icon={<Business />}
            color="#f59e0b"
            description="in pipeline"
            onClick={() => navigate(`${basePath}/crm-leads`)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Orders"
            value={stats.ordersThisMonth || '--'}
            icon={<ShoppingCart />}
            color="#3b82f6"
            description="this month"
            onClick={() => navigate(`${basePath}/orders`)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Performance"
            value={stats.totalRevenue ? `$${stats.totalRevenue.toLocaleString()}` : '--'}
            icon={<TrendingUp />}
            color="#10b981"
            description="view analytics"
            onClick={() => navigate(`${basePath}/analytics`)}
          />
        </Grid>
      </Grid>

      {/* Quick Links */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              mb: 3, 
              color: '#212121',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Quick Actions
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: '1px solid #ececef',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 4,
                borderColor: '#0d9488'
              }
            }}
            onClick={() => navigate(`${basePath}/crm-leads`)}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business sx={{ fontSize: 32, color: '#0d9488', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                  Create New Lead
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                Add a new prospect to your CRM pipeline
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: '1px solid #ececef',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 4,
                borderColor: '#0d9488'
              }
            }}
            onClick={() => navigate(`${basePath}/inventory`)}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Inventory2 sx={{ fontSize: 32, color: '#0d9488', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                  View Inventory
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                Check your stock and client inventory levels
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: '1px solid #ececef',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: 4,
                borderColor: '#0d9488'
              }
            }}
            onClick={() => navigate(`${basePath}/analytics`)}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 32, color: '#0d9488', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                  View Analytics
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                Track your sales performance and metrics
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RepDashboard;
